import React, {useEffect, useState, useRef} from 'react'
import PageApp from '../../../Page';
import { HomeOutlined, UnorderedListOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import {Form, Button, message, Skeleton, Row, Table, Card, Statistic, Col, Tag, Select} from 'antd';
import {getAll, drop} from '../../../../apis/DefinicionPolizaApi';
import {processPoliza} from '../../../../apis/PolizaApi';
import AnioPeriodo from '../../../../components/forms/anio_periodo';
import _ from 'lodash'

const { Option } = Select;

const routes = [
  {
    href: '',
    title: <>
      <HomeOutlined />
      <span>Inicio</span>
    </>,
  },
  {
    href: '',
    title: (
      <>
        <UnorderedListOutlined />
        <span>Generar Poliza</span>
      </>
    ),
  }
];

const GenerarPoliza = () => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false);
  const [loadingDefiniciones, setLoadingDefiniciones] = useState(false);
  const [objectSelected, setObjectSelected] = useState(null);
  const [disabledForm, setDisabled] = useState(true);
  const [header, setHeader] = useState(null);
  const [period, setPeriod] = useState(null);
  const [polizaGenerada, setPolizaGenerada] = useState(false);
  const [polizaEnviando, setPolizaEnviando] = useState(false);
  const [polizaData, setPolizaData] = useState(null);
  const [definicionesPoliza, setDefinicionesPoliza] = useState([]);
  const [definicionSeleccionada, setDefinicionSeleccionada] = useState(null);

  // Cargar definiciones de póliza al montar el componente
  useEffect(() => {
    loadDefinicionesPoliza();
  }, []);

  const loadDefinicionesPoliza = async () => {
    setLoadingDefiniciones(true);
    try {
      const response = await getAll();
      if (response && response.ok && response.payload) {
        setDefinicionesPoliza(response.payload);
      } else {
        message.error('No se pudieron cargar las definiciones de póliza');
      }
    } catch (error) {
      console.error('Error al cargar definiciones de póliza:', error);
      message.error('Error al cargar las definiciones de póliza');
    } finally {
      setLoadingDefiniciones(false);
    }
  };

  const handleDefinicionChange = (value) => {
    setDefinicionSeleccionada(value);
    const definicion = definicionesPoliza.find(d => d.id === value);
  };

  // Función para procesar los datos de la API y calcular totales
  const processPolizaData = (apiResponse) => {
    if (!apiResponse || !Array.isArray(apiResponse)) {
      return null;
    }

    // Agrupar por cuenta y sumar los valores
    const cuentasAgrupadas = {};
    
    apiResponse.forEach((item, index) => {
      const cuenta = item.cuenta || 'Sin cuenta';
      
      if (!cuentasAgrupadas[cuenta]) {
        cuentasAgrupadas[cuenta] = {
          key: cuenta,
          cuenta: item.concepto_clave || '',
          descripcion: cuenta,
          cargo: 0,
          abono: 0,
          conceptos: []
        };
      }
      
      cuentasAgrupadas[cuenta].cargo += item.cargo || 0;
      cuentasAgrupadas[cuenta].abono += item.abono || 0;
      cuentasAgrupadas[cuenta].conceptos.push({
        clave: item.concepto_clave,
        concepto: item.concepto,
        cargo: item.cargo || 0,
        abono: item.abono || 0
      });
    });

    const cargos = Object.values(cuentasAgrupadas);
    
    // Calcular totales
    const totalCargos = cargos.reduce((sum, item) => sum + (item.cargo || 0), 0);
    const totalAbonos = cargos.reduce((sum, item) => sum + (item.abono || 0), 0);
    const balance = totalCargos - totalAbonos;

    return {
      cargos,
      resumen: {
        totalCargos: totalCargos,
        totalAbonos: totalAbonos,
        balance: balance
      },
      detalleConceptos: apiResponse // Guardamos el detalle original
    };
  };

  const handleGenerarPoliza = async () => {
    if (!definicionSeleccionada) {
      message.warning('Por favor selecciona una definición de póliza');
      return;
    }

    if (!header || !header.periodo_id || !header.anio || !header.num_periodo) {
      message.warning('Por favor completa los datos del período');
      return;
    }

    setLoading(true);
    
    try {
      // Llamada real a la API
      const response = await processPoliza(
        definicionSeleccionada,
        header.periodo_id,
        header.anio,
        header.num_periodo
      );

      if (response && response.ok && response.payload) {
        const processedData = processPolizaData(response.payload);
        if (processedData) {
          setPolizaData(processedData);
          setPolizaGenerada(true);
          message.success('Póliza generada correctamente!');
        } else {
          message.error('Error al procesar los datos de la póliza');
        }
      } else {
        message.error(response?.message || 'Error al generar la póliza');
      }
    } catch (error) {
      console.error('Error al generar póliza:', error);
      message.error('Error al generar la póliza');
    } finally {
      setLoading(false);
    }
  };

  const sendToOdoo = async () => {
    setLoading(true);
    
    // Simular envío a Odoo
    setTimeout(() => {
      message.success('Póliza enviada a Odoo correctamente!');
      setLoading(false);
      // Resetear el estado después del envío
      setPolizaEnviando(false);
      setPolizaData(null);
    }, 1500);
  };

  const resetBody = async () => {
    let values = await form.getFieldValue();
    form.resetFields();
    form.setFieldsValue({
      periodo_id: values.periodo_id,
      anio: values.anio,
      num_periodo: values.num_periodo,
    });
    setPolizaGenerada(false);
    setPolizaEnviando(false)
    setPolizaData(null);
    setDefinicionSeleccionada(null);
  };

  const onBlurHeader = async (values, period) => {
    setHeader(values)
    if (values?.anio && values?.num_periodo && values?.periodo_id) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }

    if (period) {
      setPeriod(period);
    } else {
      setPeriod(null)
    }
  };

  const columns = [
    {
      title: 'Concepto',
      dataIndex: 'cuenta',
      key: 'cuenta',
      width: 120,
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
    },
    {
      title: 'Cargo',
      dataIndex: 'cargo',
      key: 'cargo',
      render: (value) => value > 0 ? `$${value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-',
      align: 'right',
    },
    {
      title: 'Abono',
      dataIndex: 'abono',
      key: 'abono',
      render: (value) => value > 0 ? `$${value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-',
      align: 'right',
    },
  ];

  // Columnas para la tabla de detalle de conceptos
  const conceptosColumns = [
    {
      title: 'Clave',
      dataIndex: 'concepto_clave',
      key: 'concepto_clave',
      width: 80,
    },
    {
      title: 'Concepto',
      dataIndex: 'concepto',
      key: 'concepto',
    },
    {
      title: 'Cuenta',
      dataIndex: 'cuenta',
      key: 'cuenta',
    },
    {
      title: 'Cargo',
      dataIndex: 'cargo',
      key: 'cargo',
      render: (value) => value > 0 ? `$${value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-',
      align: 'right',
    },
    {
      title: 'Abono',
      dataIndex: 'abono',
      key: 'abono',
      render: (value) => value > 0 ? `$${value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-',
      align: 'right',
    },
  ];

  const getRowClassName = (record) => {
    // Puedes agregar lógica para destacar filas específicas
    return '';
  };

  return (
    <PageApp
      routes={routes}
      title={'Generar Poliza'}
    >
      <AnioPeriodo 
        onBlur={onBlurHeader} 
        form={form} 
        disabledForm={objectSelected && objectSelected.id > 0}
      />
      
      {/* Selector de Definición de Póliza */}
      <Row style={{ marginTop: 16, marginBottom: 16 }}>
        <Col span={24}>
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontWeight: 'bold' }}>Definición de Póliza:</label>
          </div>
          <Select
            placeholder="Selecciona una definición de póliza"
            style={{ width: '100%', maxWidth: 400 }}
            value={definicionSeleccionada}
            onChange={handleDefinicionChange}
            loading={loadingDefiniciones}
            disabled={disabledForm || polizaGenerada}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {definicionesPoliza.map(definicion => (
              <Option key={definicion.id} value={definicion.id}>
                {`${definicion.clave} - ${definicion.descripcion}`}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>
      
      <Row style={{ marginTop: 16, marginBottom: 16 }}>
        <Button 
          style={{ marginRight: 10 }} 
          type="primary" 
          onClick={handleGenerarPoliza} 
          loading={loading && !polizaGenerada} 
          disabled={disabledForm || !definicionSeleccionada || polizaGenerada || !polizaEnviando}
        >
          {loading && !polizaGenerada ? 'Generando...' : 'Generar Póliza'}
        </Button>
        
        <Button 
          type="primary" 
          onClick={sendToOdoo} 
          loading={loading && polizaGenerada} 
          disabled={!polizaGenerada || !polizaEnviando}
        >
          {loading && polizaEnviando ? 'Enviando...' : 'Enviar a Odoo'}
        </Button>

        <Button 
          style={{ marginLeft: 10 }} 
          onClick={resetBody}
          disabled={loading}
        >
          Limpiar
        </Button>
      </Row>

      {loading && !polizaData ? (
        <Skeleton active={loading} />
      ) : (
        <>
          {polizaData && (
            <div>
              {/* Resumen de la póliza */}
              <Card title="Resumen de Póliza" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic 
                      title="Definición Utilizada" 
                      value={definicionesPoliza.find(d => d.id === definicionSeleccionada)?.descripcion || 'N/A'}
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="Total Cargos" 
                      value={polizaData.resumen.totalCargos} 
                      prefix="$"
                      precision={2}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="Total Abonos" 
                      value={polizaData.resumen.totalAbonos} 
                      prefix="$"
                      precision={2}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="Balance" 
                      value={polizaData.resumen.balance} 
                      prefix="$"
                      precision={2}
                      valueStyle={{ 
                        color: Math.abs(polizaData.resumen.balance) < 0.01 ? '#3f8600' : '#cf1322' 
                      }}
                    />
                  </Col>
                </Row>
              </Card>

              {/* Tabla resumen por cuenta */}
              <Card title="Resumen por Cuenta" style={{ marginBottom: 16 }}>
                <Table 
                  dataSource={polizaData.cargos} 
                  columns={columns}
                  pagination={false}
                  rowClassName={getRowClassName}
                  summary={(pageData) => {
                    let totalCargo = 0;
                    let totalAbono = 0;

                    pageData.forEach(({ cargo, abono }) => {
                      totalCargo += cargo || 0;
                      totalAbono += abono || 0;
                    });

                    return (
                      <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
                        <Table.Summary.Cell colSpan={2}>TOTALES</Table.Summary.Cell>
                        <Table.Summary.Cell align="right">
                          ${totalCargo.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right">
                          ${totalAbono.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    );
                  }}
                />
              </Card>

              {/* Tabla de detalle por conceptos */}
              <Card title="Detalle por Conceptos">
                <Table 
                  dataSource={polizaData.detalleConceptos.map((item, index) => ({
                    ...item,
                    key: index
                  }))} 
                  columns={conceptosColumns}
                  pagination={false}
                  size="small"
                />
              </Card>

              {Math.abs(polizaData.resumen.balance) >= 0.01 && (
                <Card style={{ marginTop: 16, backgroundColor: '#fff2f0', borderColor: '#ffccc7' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <ExclamationCircleFilled style={{ color: '#ff4d4f', marginRight: 8 }} />
                    <span style={{ color: '#ff4d4f' }}>
                      Advertencia: La póliza no está balanceada. 
                      Diferencia: ${Math.abs(polizaData.resumen.balance).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </Card>
              )}
            </div>
          )}
        </>
      )}

      <style jsx>{`
        .balance-row {
          background-color: #fff2f0 !important;
        }
      `}</style>
    </PageApp>
  );
};

export default GenerarPoliza;